import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { supermercadoStockService, supermercadoService, productService, extractErrorDetails } from '../services/api';
import BarcodeScanner from './BarcodeScanner';
import ErrorDisplay from './ErrorDisplay';

function SupermercadoStockReport() {
  const { user } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [supermercados, setSupermercados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  
  const [filters, setFilters] = useState({
    supermercadoId: '',
    productId: '',
    productName: '',
    stockMin: '',
    stockMax: '',
    sortDirection: 'desc'
  });
  
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0
  });

  const isSupermercado = user?.role === 'ROLE_ADMIN_SUPERMERCADO';
  const isDeposito = user?.role === 'ROLE_ADMIN_DEPOSITO';
  const isSistema = user?.role === 'ROLE_ADMIN_SISTEMA';

  // Fetch supermercados for dropdown (not for supermercado users)
  useEffect(() => {
    const fetchSupermercados = async () => {
      if (isSupermercado) return;
      
      try {
        const response = await supermercadoService.getActiveSupermercados();
        if (response.success) {
          setSupermercados(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching supermercados:', err);
      }
    };
    
    fetchSupermercados();
  }, [isSupermercado]);

  // Fetch products for search
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productService.getAllActiveProducts();
        if (response.success) {
          setAllProducts(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };
    
    fetchProducts();
  }, []);

  // Filter products based on search query
  useEffect(() => {
    if (productSearchQuery.trim() === '') {
      setFilteredProducts([]);
    } else {
      const query = productSearchQuery.toLowerCase();
      const filtered = allProducts.filter(product =>
        product.codigoBarras.toLowerCase().includes(query) ||
        product.nombre.toLowerCase().includes(query)
      );
      setFilteredProducts(filtered.slice(0, 10));
    }
  }, [productSearchQuery, allProducts]);

  const fetchReport = useCallback(async () => {
    // For non-supermercado users, require supermercado selection
    if (!isSupermercado && !filters.supermercadoId) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      
      const requestFilters = {
        ...filters,
        page: pagination.page,
        size: 20
      };
      
      // Clean empty values
      Object.keys(requestFilters).forEach(key => {
        if (requestFilters[key] === '') {
          delete requestFilters[key];
        }
      });
      
      const response = await supermercadoStockService.getStockReport(requestFilters);
      
      if (response.success) {
        const data = response.data;
        setStocks(data.content || []);
        setPagination({
          page: data.pageNumber,
          totalPages: data.totalPages,
          totalElements: data.totalElements
        });
      } else {
        setError(response.message || 'Error al cargar el reporte');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar el reporte');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, isSupermercado]);

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 0 }));
    fetchReport();
  };

  const handleClearFilters = () => {
    setFilters({
      supermercadoId: '',
      productId: '',
      productName: '',
      stockMin: '',
      stockMax: '',
      sortDirection: 'desc'
    });
    setStocks([]);
    setPagination({ page: 0, totalPages: 0, totalElements: 0 });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  useEffect(() => {
    if (pagination.page > 0 || (isSupermercado || filters.supermercadoId)) {
      fetchReport();
    }
  }, [pagination.page, fetchReport, isSupermercado, filters.supermercadoId]);

  const handleProductSelect = (product) => {
    setFilters(prev => ({
      ...prev,
      productId: product.id,
      productName: product.nombre
    }));
    setProductSearchQuery('');
    setFilteredProducts([]);
  };

  const handleClearProduct = () => {
    setFilters(prev => ({
      ...prev,
      productId: '',
      productName: ''
    }));
  };

  const handleScan = async (barcode) => {
    setShowScanner(false);
    setProductSearchQuery(barcode);
    
    try {
      const response = await productService.getProductByBarcode(barcode);
      if (response.success && response.data) {
        handleProductSelect(response.data);
      } else {
        setError('Producto no encontrado con ese código de barras');
      }
    } catch (err) {
      setError('Producto no encontrado con ese código de barras');
    }
  };

  return (
    <div className="report-container">
      <h3>Reporte de Stock por Supermercado</h3>
      
      {showScanner && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      <div className="filter-section">
        <div className="filter-row">
          {/* Supermercado selector - only for non-supermercado users */}
          {!isSupermercado && (
            <div className="filter-group">
              <label>Supermercado *</label>
              <select
                value={filters.supermercadoId}
                onChange={(e) => setFilters(prev => ({ ...prev, supermercadoId: e.target.value }))}
                className="filter-select"
              >
                <option value="">Seleccione un supermercado</option>
                {supermercados.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {/* Product search */}
          <div className="filter-group">
            <label>Producto (opcional)</label>
            {filters.productId ? (
              <div className="selected-filter-item">
                <span>{filters.productName}</span>
                <button 
                  type="button" 
                  onClick={handleClearProduct}
                  className="btn btn-small btn-secondary"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="input-with-button">
                <input
                  type="text"
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  placeholder="Buscar producto..."
                  className="filter-input"
                />
                <button 
                  type="button" 
                  onClick={() => setShowScanner(true)}
                  className="btn btn-icon"
                  title="Escanear código de barras"
                >
                  📷
                </button>
              </div>
            )}
            {filteredProducts.length > 0 && (
              <ul className="product-dropdown">
                {filteredProducts.map(product => (
                  <li 
                    key={product.id}
                    onClick={() => handleProductSelect(product)}
                    className="product-dropdown-item"
                  >
                    <span className="product-code">{product.codigoBarras}</span>
                    <span className="product-name">{product.nombre}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="filter-row">
          {/* Stock range */}
          <div className="filter-group">
            <label>Stock mínimo</label>
            <input
              type="number"
              value={filters.stockMin}
              onChange={(e) => setFilters(prev => ({ ...prev, stockMin: e.target.value }))}
              placeholder="Desde"
              className="filter-input"
            />
          </div>
          
          <div className="filter-group">
            <label>Stock máximo</label>
            <input
              type="number"
              value={filters.stockMax}
              onChange={(e) => setFilters(prev => ({ ...prev, stockMax: e.target.value }))}
              placeholder="Hasta"
              className="filter-input"
            />
          </div>

          {/* Sort direction */}
          <div className="filter-group">
            <label>Ordenar por stock</label>
            <select
              value={filters.sortDirection}
              onChange={(e) => setFilters(prev => ({ ...prev, sortDirection: e.target.value }))}
              className="filter-select"
            >
              <option value="desc">Mayor a menor</option>
              <option value="asc">Menor a mayor</option>
            </select>
          </div>
        </div>

        <div className="filter-actions">
          <button 
            onClick={handleApplyFilters} 
            className="btn btn-primary"
            disabled={!isSupermercado && !filters.supermercadoId}
          >
            Aplicar Filtros
          </button>
          <button onClick={handleClearFilters} className="btn btn-secondary">
            Limpiar Filtros
          </button>
        </div>
      </div>

      <ErrorDisplay error={error} errorTrace={errorTrace} />

      {loading ? (
        <p className="loading-text">Cargando reporte...</p>
      ) : stocks.length === 0 ? (
        <p className="no-results">
          {(!isSupermercado && !filters.supermercadoId) 
            ? 'Seleccione un supermercado y aplique los filtros para ver el reporte'
            : 'No se encontraron productos con los filtros seleccionados'}
        </p>
      ) : (
        <>
          <p className="results-count">Total: {pagination.totalElements} productos</p>
          
          <table className="report-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                {(isDeposito || isSistema) && <th>Supermercado</th>}
                <th>Stock</th>
              </tr>
            </thead>
            <tbody>
              {stocks.map(stock => (
                <tr key={stock.id || `${stock.supermercadoId}-${stock.productId}`}>
                  <td>{stock.productCodigoBarras}</td>
                  <td>{stock.productNombre}</td>
                  {(isDeposito || isSistema) && <td>{stock.supermercadoNombre}</td>}
                  <td className={stock.stock > 0 ? 'positive' : stock.stock < 0 ? 'negative' : 'zero'}>
                    {stock.stock}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="btn btn-small"
              >
                Anterior
              </button>
              <span>Página {pagination.page + 1} de {pagination.totalPages}</span>
              <button 
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="btn btn-small"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SupermercadoStockReport;
